import { map, share, shareReplay, tap } from 'rxjs/operators'
import { Observable } from 'rxjs'
import { inject, Injectable } from '@angular/core'
import { BreakpointObserver, Breakpoints as BP } from '@angular/cdk/layout'
import {
	Breakpoints,
	BreakpointSection,
	ScreenSize,
	ScreenSizes,
} from '@lg/shared'
import { ChangeDetectionService } from './change-detection.service.js'
import { toSignal } from '@angular/core/rxjs-interop'

@Injectable({
	providedIn: 'root',
})
export class LayoutService {
	private breakpointObserver = inject(BreakpointObserver)
	private cdService = inject(ChangeDetectionService)

	breakpoints$: Observable<Breakpoints> = this.breakpointObserver
		.observe([
			BP.XSmall,
			BP.Small,
			BP.Medium,
			BP.Large,
			BP.XLarge,
			BP.Handset,
			BP.HandsetPortrait,
			BP.HandsetLandscape,
			BP.Tablet,
			BP.TabletPortrait,
			BP.TabletLandscape,
			BP.Web,
			BP.WebPortrait,
			BP.WebLandscape,
		])
		.pipe(
			map((state) => {
				const queries = ScreenSizes.map((size) => BP[size])

				const matchedIdx = queries.findIndex(
					(query) => state.breakpoints[query],
				)

				const bps: Breakpoints = {
					sizeEquals: Object.fromEntries(
						ScreenSizes.map((size, idx) => [size, matchedIdx == idx]),
					) as unknown as BreakpointSection,
					sizeIsGreaterThan: Object.fromEntries(
						ScreenSizes.map((size, idx) => [size, matchedIdx > idx]),
					) as unknown as BreakpointSection,
					sizeIsGreaterThanOrEqual: Object.fromEntries(
						ScreenSizes.map((size, idx) => [size, matchedIdx >= idx]),
					) as unknown as BreakpointSection,
					sizeIsLessThan: Object.fromEntries(
						ScreenSizes.map((size, idx) => [size, matchedIdx < idx]),
					) as unknown as BreakpointSection,
					sizeIsLessThanOrEqual: Object.fromEntries(
						ScreenSizes.map((size, idx) => [size, matchedIdx <= idx]),
					) as unknown as BreakpointSection,
					isHandset: state.breakpoints[BP.Handset],
					isHandsetPortrait: state.breakpoints[BP.HandsetPortrait],
					isHandsetLandscape: state.breakpoints[BP.HandsetLandscape],
					isTablet: state.breakpoints[BP.Tablet],
					isTabletPortrait: state.breakpoints[BP.TabletPortrait],
					isTabletLandscape: state.breakpoints[BP.TabletLandscape],
					isWeb: state.breakpoints[BP.Web],
					isWebPortrait: state.breakpoints[BP.WebPortrait],
					isWebLandscape: state.breakpoints[BP.WebLandscape],
					isPortrait:
						state.breakpoints[BP.HandsetPortrait] ||
						state.breakpoints[BP.TabletPortrait] ||
						state.breakpoints[BP.WebPortrait],
					isLandscape:
						state.breakpoints[BP.HandsetLandscape] ||
						state.breakpoints[BP.TabletLandscape] ||
						state.breakpoints[BP.WebLandscape],
					screenSize: ScreenSizes[matchedIdx],
					decideBySize<T>(
						options: Partial<Record<ScreenSize, T>> | [T, T, T, T, T],
					) {
						if (options instanceof Array) {
							const idx = ScreenSizes.findIndex((ss) => ss == this.screenSize)
							return (options[idx] ?? options.at(-1))!
						}
						let lastEncounteredValue: T = options.XSmall!

						for (const screenSize of ScreenSizes) {
							if (this.sizeIsLessThan[screenSize]) {
								return lastEncounteredValue
							}
							const option = options[screenSize]
							if (option !== undefined) lastEncounteredValue = option
						}
						return lastEncounteredValue
					},
				}
				return bps
			}),
			tap((bps) => {
				// TODO: change to computed signal and use on template
				document.body.classList.remove(...ScreenSizes)
				document.body.classList.add(bps.screenSize)

				this.cdService.refreshWholeApp()
			}),
			shareReplay(1),
		)
	breakpoints = toSignal(this.breakpoints$, { initialValue: {} as Breakpoints })
}
